/**
 * Navigation and UI Handler Utilities
 *
 * This file contains utility functions for navigation and UI interactions
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

/**
 * Navigate back with state information
 * @param {string} snapshot - Canvas snapshot data
 * @param {Array} updatedImages - Array of updated images
 * @param {string} pageId - Current page ID
 * @param {Object} elementCounts - Count of different element types
 * @param {Function} navigate - React Router navigate function
 */
export const createNavigateWithState =
  (pageId, navigate) => (snapshot, updatedImages, elementCounts) => {
    const navigationState = {
      snapshot,
      hasChanges: true,
      pageId,
      fromMyCanvas: true,
      elementCounts: {
        texts: elementCounts.texts?.length || 0,
        images: updatedImages?.length || 0,
        rectangles: elementCounts.rectangles?.length || 0,
        circles: elementCounts.circles?.length || 0,
        arrows: elementCounts.arrows?.length || 0,
        lines: elementCounts.lines?.length || 0,
        doubleSidedArrows: elementCounts.doubleSidedArrows?.length || 0,
        scribbles: elementCounts.scribbles?.length || 0,
      },
    };

    // Set localStorage items
    localStorage.setItem("fromMyCanvas", "true");
    localStorage.setItem("pageId", pageId);
    localStorage.setItem(
      "elementCounts",
      JSON.stringify(navigationState.elementCounts)
    );

    navigate(-1, { state: navigationState });
  };

/**
 * Handle sorting functionality for tables/lists
 * @param {string} field - Field to sort by
 * @param {string} sortField - Current sort field
 * @param {string} sortDirection - Current sort direction
 * @param {Function} setSortField - Setter for sort field
 * @param {Function} setSortDirection - Setter for sort direction
 */
export const createHandleSort =
  (sortField, sortDirection, setSortField, setSortDirection) => (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

/**
 * Handle editing a part
 * @param {Object} part - Part object to edit
 * @param {Function} setEditingPartId - Setter for editing part ID
 * @param {Function} setEditedPartData - Setter for edited part data
 */
export const createHandleEditPart =
  (setEditingPartId, setEditedPartData) => (part) => {
    setEditingPartId(part.part_id);
    setEditedPartData({
      ...part,
      type_name: part.type_details?.name,
      unit_type_id_ref: part.unit_type_id_ref,
    });
  };
